import { UseFormReturn } from "react-hook-form";
import { Check } from "lucide-react";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";

export type Props = {
  form: UseFormReturn<{ code: string }>;
  isSubmitting: boolean;
  isValid: boolean;
  isSubmitSuccessful: boolean;
  onSubmit: () => void;
};

/**
 * Inline form for entering an invitation code.
 * Displays input field with submit button, shows success message when code is found.
 */
export function InviteCodeForm({
  form,
  isSubmitting,
  isValid,
  isSubmitSuccessful,
  onSubmit,
}: Props) {
  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      className="w-full"
      innerClassName="gap-0"
      errorContainerClassName="mt-4 p-0 max-w-sm mx-0 text-error-400"
      data-testid="invite-code-form"
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full">
        <div className="flex-1 sm:max-w-sm">
          <FormField
            name="code"
            component="Input"
            placeholder="Enter your invite code"
            required
            isHideErrorMessage
            transformOnChange={(val: string) =>
              val.toUpperCase().replace(/\s+/g, "")
            }
            disabled={isSubmitting || isSubmitSuccessful}
            theme="dark"
            size="hero"
            success={isSubmitSuccessful}
          />
        </div>
        <Button
          type="submit"
          theme="dark"
          size="hero"
          disabled={isSubmitting || isSubmitSuccessful}
          visuallyDisabled={!isSubmitting && !isSubmitSuccessful && !isValid}
          isLoading={isSubmitting}
          iconComponent={isSubmitSuccessful ? Check : undefined}
          className="shrink-0 min-w-32 [&_svg]:text-success"
          typeClassName="md:font-medium"
          data-testid={isSubmitSuccessful ? "invite-code-form-success" : "invite-code-submit"}
        >
          {isSubmitSuccessful ? null : "Join"}
        </Button>
      </div>
      {isSubmitSuccessful && (
        <Type variant="body2" className="mt-3 text-brand-200 animate-pulse">
          Invite code found, redirecting...
        </Type>
      )}
    </Form>
  );
}

export default InviteCodeForm;
